import { Grid, TextField, Typography } from "@material-ui/core";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import { Autocomplete } from "@material-ui/lab";
import parse from "autosuggest-highlight/parse";
import throttle from "lodash/throttle";
import React, { useEffect, useMemo, useRef, useState } from "react";

const API_URL =
  "https://maps.googleapis.com/maps/api/js?key=" +
  process.env.REACT_APP_GMAPS_PLACES_KEY +
  "&libraries=places";

const loadScript = (src, position, id) => {
  if (!position) {
    return;
  }

  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", id);
  script.src = src;
  position.appendChild(script);
};

const autocompleteService = { current: null };

const SelectAddress = ({
  className,
  variant,
  size,
  label,
  placeholder,
  disabled,
  value = "",
  resetOnSelect = false,
  onChange = () => {},
  onBlur = () => {},
  onInputChange = () => {}
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [options, setOptions] = useState([]);
  const loaded = useRef(false);

  if (typeof window !== "undefined" && !loaded.current) {
    if (!document.querySelector("#google-maps")) {
      loadScript(API_URL, document.querySelector("head"), "google-maps");
    }

    loaded.current = true;
  }

  const handleChange = event => {
    setInputValue(event.target.value);
    onInputChange(event.target.value);
  };

  const handleSelect = (_, place) => {
    if (resetOnSelect) {
      setInputValue("");
    } else {
      setInputValue(place);
    }
    onChange(place);
  };

  const fetch = useMemo(
    () =>
      throttle((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 200),
    []
  );

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current && window.google) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions([]);
      return undefined;
    }

    const requestInfo = {
      input: inputValue,
      types: ["address"],
      componentRestrictions: { country: "cl" }
    };

    fetch(requestInfo, results => {
      if (results && active) {
        setOptions([...results, { image: "google" }] || [{ image: "google" }]);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue, fetch]);

  return (
    <Autocomplete
      className={className}
      id="address-autocomplete"
      getOptionLabel={option => (typeof option === "string" ? option : option.description)}
      filterOptions={x => x}
      options={options}
      disabled={disabled}
      autoComplete
      clearOnBlur
      includeInputInList
      value={inputValue}
      onChange={handleSelect}
      onBlur={onBlur}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant={variant}
          size={size}
          InputLabelProps={{
            shrink: true
          }}
          onChange={handleChange}
        />
      )}
      getOptionDisabled={option => option === options[options.length - 1]}
      renderOption={option => {
        if (!option.image) {
          const matches = option.structured_formatting.main_text_matched_substrings;
          const parts = parse(
            option.structured_formatting.main_text,
            matches.map(match => [match.offset, match.offset + match.length])
          );

          return (
            <Grid container alignItems="center">
              <Grid item className="mr-2">
                <LocationOnIcon />
              </Grid>
              <Grid item xs>
                {parts.map((part, index) => (
                  <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                    {part.text}
                  </span>
                ))}

                <Typography variant="body2" color="textSecondary">
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          );
        } else {
          return (
            <div className="w-100">
              <img
                align="right"
                src={process.env.PUBLIC_URL + "/assets/images/powered_by_google_on_white.png"}
                alt="Powered by Google"
              />
            </div>
          );
        }
      }}
    />
  );
};

export default SelectAddress;
